import { ArrowLeft, Search } from 'lucide-react';

import type { ConversionState, ScanFieldKey } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { TEXT_LIMITS } from '@splitfiesta/shared';
import { Button, Card, FormField } from '@splitfiesta/ui';

import { ConversionPreview } from '@/components/expenses/ConversionPreview';
import type { ConversionPreviewInfo } from '@/components/expenses/ConversionPreview';
import { AiTag } from '@/components/expenses/receipt/AiTag';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { LinkButton } from '@/components/ui/LinkButton';
import SearchableCurrencySelect from '@/components/ui/SearchableCurrencySelect';

import { getInputClasses } from '@/lib/styles/form';

import { ExpenseBasicInfoService } from '@/services/expense/ExpenseBasicInfoService';
import type {
  CategoryData,
  SubcategoryIconEntry,
} from '@/services/expense/ExpenseBasicInfoService';

interface ExpenseBasicInfoPresentationProps {
  // Form values
  title: string;
  setTitle: (value: string) => void;
  /** Title field lost focus — the form flushes the cloud prediction. */
  onTitleBlur?: () => void;
  /** The group's fixed currency — the target of any foreign-currency conversion. */
  groupCurrency: string;
  /** The currency the amount is being entered in. */
  enteredCurrency: string;
  onCurrencyChange: (value: string) => void;
  amount: string | undefined;
  onAmountChange: (value: string | undefined, numericValue: number) => void;
  isLoading: boolean;
  showCategory: boolean;

  // Foreign-currency conversion
  /** What the user typed, in `enteredCurrency`. */
  enteredAmount: number;
  conversion: ConversionState;
  preview: ConversionPreviewInfo;
  onManualRateChange: (rate: number | null) => void;
  isOfflineQueue: boolean;
  /** The expense date, used for the offline-queued conversion message. */
  date: string;
  /** Fields the receipt scan filled; each shows the AI tag after its label. */
  aiTags?: ReadonlySet<ScanFieldKey>;
  /** The scan could not trust the receipt's currency. */
  checkCurrency?: boolean;

  // Category data
  category: string;
  subcategory?: string;
  categories: CategoryData[];
  currentCategory: CategoryData;

  // UI state
  showAllCategories: boolean;
  showSubcategoryIcons: boolean;
  selectedCategoryForIcons: string;
  subcategorySearch: string;
  setSubcategorySearch: (value: string) => void;
  filteredSubcategoryIcons: SubcategoryIconEntry[];

  // Handlers
  onCategorySelect: (categoryValue: string) => void;
  onSubcategorySelect: (subcategoryValue: string) => void;
  onShowAllCategories: () => void;
  onCancelCategorySelection: () => void;
  onBackToCategories: () => void;

  // Refs
  categoryRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Presentation component for expense basic info
 * Pure UI component with no business logic
 */
export function ExpenseBasicInfoPresentation({
  title,
  setTitle,
  onTitleBlur,
  groupCurrency,
  enteredCurrency,
  onCurrencyChange,
  amount,
  onAmountChange,
  isLoading,
  showCategory,
  enteredAmount,
  conversion,
  preview,
  onManualRateChange,
  isOfflineQueue,
  date,
  aiTags,
  checkCurrency = false,
  category,
  subcategory,
  categories,
  currentCategory,
  showAllCategories,
  showSubcategoryIcons,
  selectedCategoryForIcons,
  subcategorySearch,
  setSubcategorySearch,
  filteredSubcategoryIcons,
  onCategorySelect,
  onSubcategorySelect,
  onShowAllCategories,
  onCancelCategorySelection,
  onBackToCategories,
  categoryRef,
}: ExpenseBasicInfoPresentationProps) {
  const { t } = useTranslation('expenses');
  const tagAfterLabel = (field: ScanFieldKey) =>
    aiTags?.has(field) ? (
      <span className="ml-2 align-middle">
        <AiTag />
      </span>
    ) : null;

  return (
    <Card>
      <div className="space-y-4">
        {/* Amount and Currency */}
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label
                htmlFor="amount"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t('fields.amount')} <span className="text-error-500">*</span>
                {tagAfterLabel('amount')}
                {checkCurrency ? (
                  <span className="ml-2 text-xs font-medium text-warning-700 dark:text-warning-400">
                    {t('scan.checkCurrency')}
                  </span>
                ) : null}
              </label>
              <CurrencyInput
                id="amount"
                name="amount"
                value={amount}
                currency={enteredCurrency}
                onValueChange={(value, _name, values) => {
                  // Pass the numeric value extracted from the values object
                  const numericValue = values?.float ?? 0;
                  onAmountChange(value, numericValue);
                }}
                className="text-2xl! font-bold!"
                disabled={isLoading}
                required
                autoFocus
              />
            </div>
            <div>
              <label
                htmlFor="currency"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t('fields.enteredCurrency')}{' '}
                <span className="text-error-500">*</span>
              </label>
              <SearchableCurrencySelect
                id="currency"
                value={enteredCurrency}
                onChange={onCurrencyChange}
                mode="expense"
                required
                disabled={isLoading}
                data-testid="expense-currency-select"
              />
            </div>
          </div>
          {conversion.isForeign && (
            <ConversionPreview
              groupCurrency={groupCurrency}
              enteredCurrency={enteredCurrency}
              enteredAmount={enteredAmount}
              conversion={conversion}
              preview={preview}
              onManualRateChange={onManualRateChange}
              isOfflineQueue={isOfflineQueue}
              disabled={isLoading}
              date={date}
            />
          )}
        </div>

        {/* Title */}
        <div>
          <FormField
            id="title"
            type="text"
            label={t('fields.title')}
            labelAccessory={tagAfterLabel('title')}
            value={title}
            onChange={setTitle}
            onBlur={onTitleBlur}
            placeholder={t('placeholders.title')}
            required
            disabled={isLoading}
            maxLength={TEXT_LIMITS.EXPENSE_TITLE_MAX}
            autoComplete="off"
          />
        </div>

        {/* Category Selection */}
        {showCategory && (
          <div ref={categoryRef}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('fields.category')}
              {tagAfterLabel('category')}
            </label>

            {!showAllCategories && !showSubcategoryIcons ? (
              <CategoryDisplay
                currentCategory={currentCategory}
                subcategory={subcategory}
                isLoading={isLoading}
                onShowAllCategories={onShowAllCategories}
                t={t}
              />
            ) : showSubcategoryIcons ? (
              <SubcategorySelector
                selectedCategoryForIcons={selectedCategoryForIcons}
                category={category}
                subcategory={subcategory}
                categories={categories}
                subcategorySearch={subcategorySearch}
                setSubcategorySearch={setSubcategorySearch}
                filteredSubcategoryIcons={filteredSubcategoryIcons}
                isLoading={isLoading}
                onBackToCategories={onBackToCategories}
                onSubcategorySelect={onSubcategorySelect}
                t={t}
              />
            ) : (
              <CategorySelector
                categories={categories}
                category={category}
                isLoading={isLoading}
                onCategorySelect={onCategorySelect}
                onCancelCategorySelection={onCancelCategorySelection}
                t={t}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Category display component
 */
function CategoryDisplay({
  currentCategory,
  subcategory,
  isLoading,
  onShowAllCategories,
  t,
}: {
  currentCategory: CategoryData;
  subcategory?: string;
  isLoading: boolean;
  onShowAllCategories: () => void;
  t: (
    key: string,
    options?: { count?: number; defaultValue?: string }
  ) => string;
}) {
  return (
    <Button
      variant="ghost"
      size="lg"
      type="button"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onShowAllCategories();
      }}
      className="flex items-center space-x-3 p-3 bg-[#FCFCFA] dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 justify-start w-full"
      disabled={isLoading}
    >
      <currentCategory.icon className="w-8 h-8 text-orange-600 dark:text-orange-400" />
      <div className="flex flex-col text-left">
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {currentCategory.label}
        </span>
        {subcategory && subcategory !== 'default' && (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {ExpenseBasicInfoService.formatSubcategoryName(
              subcategory,
              currentCategory.value,
              t
            )}
          </span>
        )}
      </div>
    </Button>
  );
}

/**
 * Category selector component
 */
function CategorySelector({
  categories,
  category,
  isLoading,
  onCategorySelect,
  onCancelCategorySelection,
  t,
}: {
  categories: CategoryData[];
  category: string;
  isLoading: boolean;
  onCategorySelect: (categoryValue: string) => void;
  onCancelCategorySelection: () => void;
  t: (
    key: string,
    options?: { count?: number; defaultValue?: string }
  ) => string;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('helpers.aiCategoryDetection')}
      </p>
      <div className="flex justify-end">
        <LinkButton
          onClick={onCancelCategorySelection}
          variant="primary"
          size="base"
        >
          {t('actions.cancel')}
        </LinkButton>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <Button
              key={cat.value}
              variant="ghost"
              size="md"
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onCategorySelect(cat.value);
              }}
              disabled={isLoading}
              className={ExpenseBasicInfoService.getCategoryButtonClasses(
                category === cat.value
              )}
            >
              <Icon
                className={ExpenseBasicInfoService.getIconClasses(
                  category === cat.value
                )}
              />
              <span className="text-xs text-center leading-tight px-1 flex-1 flex items-center">
                {cat.label}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Subcategory selector component
 */
function SubcategorySelector({
  selectedCategoryForIcons,
  category,
  subcategory,
  categories,
  subcategorySearch,
  setSubcategorySearch,
  filteredSubcategoryIcons,
  isLoading,
  onBackToCategories,
  onSubcategorySelect,
  t,
}: {
  selectedCategoryForIcons: string;
  category: string;
  subcategory?: string;
  categories: CategoryData[];
  subcategorySearch: string;
  setSubcategorySearch: (value: string) => void;
  filteredSubcategoryIcons: SubcategoryIconEntry[];
  isLoading: boolean;
  onBackToCategories: () => void;
  onSubcategorySelect: (subcategoryValue: string) => void;
  t: (
    key: string,
    options?: { count?: number; defaultValue?: string }
  ) => string;
}) {
  const selectedCategoryLabel = categories.find(
    c => c.value === selectedCategoryForIcons
  )?.label;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('helpers.aiCategoryDetection')}
      </p>
      <div className="flex justify-end">
        <LinkButton
          onClick={onBackToCategories}
          variant="primary"
          size="base"
          disabled={isLoading}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('actions.backToCategories')}
        </LinkButton>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
        {(t('labels.chooseIcon') || '').replace(
          '{{category}}',
          selectedCategoryLabel || ''
        )}
      </p>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
        <input
          type="text"
          placeholder={t('placeholders.searchSubcategories')}
          value={subcategorySearch}
          onChange={e => setSubcategorySearch(e.target.value)}
          className={`${getInputClasses({ size: 'sm' })} pl-10`}
        />
      </div>

      {/* Subcategory icons grid */}
      {filteredSubcategoryIcons.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Search className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
          <p className="text-sm">No subcategories found</p>
          <p className="text-xs">Try a different search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {filteredSubcategoryIcons.map(([key, Icon]) => (
            <Button
              key={key}
              variant="ghost"
              size="md"
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onSubcategorySelect(key);
              }}
              disabled={isLoading}
              className={ExpenseBasicInfoService.getCategoryButtonClasses(
                subcategory === key && category === selectedCategoryForIcons,
                true
              )}
              data-testid={`expense-subcategory-${key}`}
            >
              <Icon
                className={ExpenseBasicInfoService.getIconClasses(
                  subcategory === key && category === selectedCategoryForIcons,
                  true
                )}
              />
              <span
                className={ExpenseBasicInfoService.getSubcategoryTextClasses(
                  subcategory === key && category === selectedCategoryForIcons
                )}
              >
                {ExpenseBasicInfoService.formatSubcategoryName(
                  key,
                  selectedCategoryForIcons,
                  t
                )}
              </span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
